<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { formatTime } from '@/utils/datetime'
import { API, HOST } from '@/utils/http'
import MapLayerPopup from '@/components/map/MapLayerPopup.vue'
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel'

const props = defineProps({
  track: {
    type: Object,
    required: true
  },
  code: {
    type: String,
    required: true
  },
  map: {
    type: Object,
    required: true
  },
  orgId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['popup-closed'])
const userStore = useUserStore()
const settings = computed(() => userStore.settings)

const photos = ref([])
const photosLoaded = ref(false)
const isLoadingPhotos = ref(false)

const hasPhotos = computed(() => props.track.has_photos === 1)

const popupCoordinates = computed(() => {
  const td = props.track.track_detection
  return td ? [td.lon, td.lat] : null
})

const emblaMainApi = ref(null)
const emblaThumbnailApi = ref(null)
const selectedPhotoIndex = ref(0)

function onSelect() {
  if (!emblaMainApi.value || !emblaThumbnailApi.value)
    return

  selectedPhotoIndex.value = emblaMainApi.value.selectedScrollSnap()
  emblaThumbnailApi.value.scrollTo(selectedPhotoIndex.value)

  nextTick(() => {
    selectedPhotoIndex.value = emblaMainApi.value.selectedScrollSnap()
  })
}

function onThumbClick(index) {
  if (!emblaMainApi.value)
    return
  emblaMainApi.value.scrollTo(index)
}

watch(emblaMainApi, (api) => {
  if (!api) return

  selectedPhotoIndex.value = api.selectedScrollSnap() || 0

  api.on('select', onSelect)
  api.on('reInit', onSelect)
}, { immediate: true })

const loadPhotos = async () => {
  if (!hasPhotos.value || photosLoaded.value || isLoadingPhotos.value) return

  isLoadingPhotos.value = true

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${props.orgId}/layers/${props.code}/radar/tracks/${props.track.id}/photos`
    })

    if (response.data && response.data.photos) {
      photos.value = response.data.photos.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at)
      })
    }
    photosLoaded.value = true
  } catch (e) {
    console.error(`Error fetching photos for track ${props.track.id}:`, e)
  } finally {
    isLoadingPhotos.value = false
  }
}

const closePopup = () => {
  emit('popup-closed', props.track.id)
}

onMounted(() => {
  if (hasPhotos.value) {
    loadPhotos()
  }
})
</script>

<template>
  <MapLayerPopup
    v-if="popupCoordinates"
    :map="map"
    :coordinates="popupCoordinates"
    :show="true"
    :offset="10"
    :max-width="'500px'"
    :min-width="'400px'"
    @close="closePopup"
  >
    <div class="text-[14px] font-sans px-3 py-2 min-w-[300px]">
      <div class="">
        <h3 class="font-bold text-lg">#{{ track.radar_track_id }}</h3>
        <ul class="max-w-[275px]">
          <li class="flex justify-between">
            <span class="font-medium text-gray-500">Start:</span>
            <span>{{ formatTime(track.started, settings.tz, 'MMM DD, YYYY HH:mm') }}</span>
          </li>
          <li class="flex justify-between">
            <span class="font-medium text-gray-500">Last update:</span>
            <span>{{ formatTime(track.last_update, settings.tz, 'MMM DD, YYYY HH:mm') }}</span>
          </li>
          <li class="flex justify-between">
            <span class="font-medium text-gray-500">Speed:</span>
            <span>{{ track.track_detection.speed }} kts</span>
          </li>
          <li class="flex justify-between">
            <span class="font-medium text-gray-500">Travel distance:</span>
            <span>{{ track.running_distance.toFixed(2) }} km</span>
          </li>
        </ul>
      </div>

      <div v-if="hasPhotos" class="mt-2">
        <h4 class="font-bold mb-1">Photos</h4>
        <div v-if="!photosLoaded" class="text-center py-2">
          <span class="text-gray-500">Loading photos...</span>
        </div>
        <div v-else-if="photos.length > 0" class="relative">
          <!-- Main Carousel -->
          <Carousel
            class="w-full mb-2"
            @init-api="(val) => emblaMainApi = val"
            @select="onSelect"
          >
            <CarouselContent>
              <CarouselItem v-for="photo in photos" :key="photo.id">
                <a :href="photo.s3_photo_link" target="_blank" class="block">
                  <img
                    :src="photo.s3_photo_link"
                    alt="Track photo"
                    class="w-full aspect-16/9 object-cover rounded shadow cursor-pointer hover:opacity-90"
                  />
                </a>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious class="absolute top-1/2 left-2 -translate-y-1/2" />
            <CarouselNext class="absolute top-1/2 right-2 -translate-y-1/2" />
          </Carousel>

          <!-- Thumbnail Carousel -->
          <Carousel
            v-if="photos.length > 1"
            class="w-full"
            @init-api="(val) => emblaThumbnailApi = val"
          >
            <CarouselContent class="flex gap-1 ml-0">
              <CarouselItem
                v-for="(photo, index) in photos"
                :key="photo.id"
                class="pl-0 basis-1/5 cursor-pointer"
                @click="onThumbClick(index)"
              >
                <div
                  class="p-1 transition-all duration-200 transform"
                >
                  <img
                    :src="photo.s3_photo_link"
                    alt="Thumbnail"
                    class="w-full aspect-square object-cover rounded"
                    :class="[
                      index === selectedPhotoIndex
                        ? 'outline-2 outline-primary rounded scale-105'
                        : 'opacity-60 hover:opacity-80'
                    ]"
                  />
                  <div class="text-xs text-white absolute bottom-1 left-1 right-1 text-center bg-black/20 rounded m-0.5 px-1 py-0.5">
                    {{ formatTime(photo.created_at, settings.tz, 'HH:mm:ss') }}
                  </div>
                </div>
              </CarouselItem>
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </div>
  </MapLayerPopup>
</template>

<style>
</style>
